import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { useSelectedProgramFilter } from ':src/domains/programs/SelectedProgramFilterContext';
import { parseDateParam, SessionDateFilter } from ':src/domains/sessions/utils';
import { CancelledWithNoticeFilter, Sort, SupervisorTypeFilter } from ':src/graphql/enums';
import { SessionDispositionEnum } from '@motivo/guanyin/src/lookup';
import { useQueryParams } from '@motivo/guanyin/src/hooks/useQueryParams';
import useViewer from './useViewer';

function getCancelledWithNoticeFilter(
  sessionDateFilter: string | undefined,
  hasUserFilter: boolean,
): CancelledWithNoticeFilter | undefined {
  const isCancelledTab = sessionDateFilter === SessionDateFilter.CANCELLED;
  const isCompletedTab = sessionDateFilter === SessionDateFilter.TODAY_AND_PAST;

  if (isCancelledTab) {
    return hasUserFilter
      ? CancelledWithNoticeFilter.IncludeAttendeeCancelled
      : CancelledWithNoticeFilter.IncludeAllAttendeesCancelled;
  }

  if (isCompletedTab) {
    return hasUserFilter
      ? CancelledWithNoticeFilter.ExcludeAttendeeCancelled
      : CancelledWithNoticeFilter.ExcludeAllAttendeesCancelled;
  }

  return undefined;
}

const PAST_SESSION_DATE_FILTERS: string[] = [
  SessionDateFilter.TODAY_AND_PAST,
  SessionDateFilter.MISSED,
];

function getSessionDateRange({
  minDateParam,
  maxDateParam,
  sessionDateFilter,
}: {
  minDateParam?: string;
  maxDateParam?: string;
  sessionDateFilter?: string;
}): { afterDate?: string; beforeDate?: string } {
  const now = DateTime.now();
  let afterDate = minDateParam ? parseDateParam(minDateParam).startOf('day') : undefined;
  let beforeDate = maxDateParam ? parseDateParam(maxDateParam).endOf('day') : undefined;

  if (sessionDateFilter === SessionDateFilter.UPCOMING) {
    afterDate = afterDate ? DateTime.max(afterDate, now) : now;
  } else if (sessionDateFilter && PAST_SESSION_DATE_FILTERS.includes(sessionDateFilter)) {
    beforeDate = beforeDate ? DateTime.min(beforeDate, now) : now;
  }

  return {
    afterDate: afterDate?.toJSDate().toISOString(),
    beforeDate: beforeDate?.toJSDate().toISOString(),
  };
}

export function useQueryVariablesForSessions() {
  const { viewer } = useViewer();
  const { queryParams } = useQueryParams();
  const [programFilter] = useSelectedProgramFilter();

  const {
    minDate: minDateParam,
    maxDate: maxDateParam,
    sessionTypeId: sessionTypeIdParam,
    userId: userIdParam,
    sessionDateFilter: sessionDateFilterParam,
    supervisorType: supervisorTypeParam,
  } = queryParams;

  const sessionTypeId = Number(sessionTypeIdParam) || undefined;
  const userId = Number(userIdParam) || undefined;
  const { afterDate, beforeDate } = useMemo(
    () =>
      getSessionDateRange({
        minDateParam,
        maxDateParam,
        sessionDateFilter: sessionDateFilterParam,
      }),
    [minDateParam, maxDateParam, sessionDateFilterParam],
  );

  const isCancelledTab = sessionDateFilterParam === SessionDateFilter.CANCELLED;
  const hasUserFilter = Boolean(userId);

  const cancelledWithNoticeFilter = getCancelledWithNoticeFilter(
    sessionDateFilterParam,
    hasUserFilter,
  );

  const queryVariables = {
    organizationId: viewer.organizationId,
    userId,
    afterDate,
    beforeDate,
    sessionTypeId,
    noShowOnly: sessionDateFilterParam === SessionDateFilter.MISSED,
    sort: sessionDateFilterParam === SessionDateFilter.UPCOMING ? Sort.Asc : Sort.Desc,
    programId: programFilter?.id,
    cancelledWithNoticeFilter,
    supervisorType: (supervisorTypeParam as SupervisorTypeFilter) || undefined,
    // For cancelled tab, backend handles disposition filtering via the cancelledWithNoticeFilter
    ...(isCancelledTab
      ? {}
      : {
          sessionDispositionIds: [
            SessionDispositionEnum.INITIAL,
            SessionDispositionEnum.COMPLETE,
            SessionDispositionEnum.CHARGED,
          ],
        }),
  };
  return queryVariables;
}
